import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  Button,
  Table,
  TableCell,
  TableRow,
  TitleSecondary,
} from '../../components';

import { config } from '../../api/config';

import { actions } from '../../state';

import {
  formatDate,
  formatMoney,
  mapOrderStatus,
  mapOrderStatusColor,
  mapPaymentMethod,
  mapServiceTypes,
} from '../../utils';

import styles from './clients.module.scss';

const PAGE_SIZE = config.DEFAULTS.recordsPerPage;

const renderTypes = types =>
  types.map(type => <p key={type}>{mapServiceTypes(type)}</p>);

const ClientOrders = ({
  dispatch,
  client_id,
  orders: { loading, orders, total, footer },
}) => {
  const [page, setPage] = useState(1);
  const pageCount = Math.ceil(total / PAGE_SIZE) || 1;

  useEffect(() => {
    if (page > pageCount) {
      setPage(1);
    }
  }, [page, pageCount]);

  const fetchOrders = useCallback(
    data => {
      dispatch(actions.orders.getOrdersAction(data));
    },
    [dispatch]
  );

  const clearOrders = useCallback(() => {
    dispatch(actions.orders.clearOrdersAction());
  }, [dispatch]);

  useEffect(() => () => clearOrders(), [clearOrders]);

  useEffect(() => {
    const filterToSubmit = {
      filter: {
        client_id,
      },
    };
    const offset = (page - 1) * PAGE_SIZE;
    fetchOrders({ ...(filterToSubmit || {}), offset });
  }, [fetchOrders, client_id, page]);

  const tableHeader = [
    '#',
    'Statusas',
    'Mokėjimo metodas',
    'Plovykla',
    'Data',
    'Įmonė',
    'Įmonės k.',
    'Nuolaida',
    'Telefonas',
    '€ su PVM',
    'Paslaugos',
    'Šaltinis',
    'Išorinis ID',
  ];

  const renderTable = () =>
    orders.map(
      ({
        id,
        status,
        payment_method,
        payment_card_type,
        location,
        created_at,
        company_name,
        company_code,
        discounts,
        client_phone,
        total_cost,
        service_types,
        source,
        external_id,
      }) => {
        return (
          <TableRow url={config.APP_PATHS.orderDetails(id)} key={id}>
            <TableCell>{id}</TableCell>
            <TableCell>
              <span style={{ color: mapOrderStatusColor(status) }}>
                {mapOrderStatus(status)}
              </span>
            </TableCell>
            <TableCell>
              {mapPaymentMethod(payment_method, payment_card_type)}
            </TableCell>
            <TableCell>{location}</TableCell>
            <TableCell>{formatDate(created_at)}</TableCell>
            <TableCell>{company_name}</TableCell>
            <TableCell>{company_code}</TableCell>
            <TableCell>
              {discounts.map((discount, i) => (
                <p key={i}>{discount}</p>
              ))}
            </TableCell>
            <TableCell>{client_phone}</TableCell>
            <TableCell align="right">{formatMoney(total_cost)}</TableCell>
            <TableCell>{renderTypes(service_types)}</TableCell>
            <TableCell>{source}</TableCell>
            <TableCell>{external_id || '-'}</TableCell>
          </TableRow>
        );
      }
    );

  const tableFooter = [
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    '',
    formatMoney(footer.total_cost),
    '',
    '',
    '',
  ];

  return (
    <div className={styles.clientOrdersContainer}>
      <div className={styles.clientOrdersHeader}>
        <TitleSecondary>Užsakymai</TitleSecondary>
        <Button
          color="outline"
          url={config.APP_PATHS.ordersByClientId(client_id)}
        >
          Visi užsakymai
        </Button>
      </div>
      <Table
        header={tableHeader}
        footer={tableFooter}
        filter={true}
        isEmpty={!orders.length}
        loading={loading}
        rightAlignedFooterCells={[8, 9]}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        serverPagination={true}
        onPageChange={setPage}
      >
        {renderTable()}
      </Table>
    </div>
  );
};

const mapStateToProps = state => ({
  orders: state.orders,
});

ClientOrders.propTypes = {
  dispatch: PropTypes.func.isRequired,
  orders: PropTypes.shape({
    orders: PropTypes.array.isRequired,
    total: PropTypes.number.isRequired,
    header: PropTypes.shape({
      status: PropTypes.arrayOf(PropTypes.number),
      location: PropTypes.arrayOf(PropTypes.string),
      payment_type: PropTypes.arrayOf(PropTypes.number),
      company_name: PropTypes.arrayOf(PropTypes.string),
      company_code: PropTypes.arrayOf(PropTypes.string),
      discounts: PropTypes.arrayOf(PropTypes.string),
      client_phone: PropTypes.arrayOf(PropTypes.string),
      service_types: PropTypes.arrayOf(PropTypes.number),
    }).isRequired,
    footer: PropTypes.shape({
      total_cost: PropTypes.number,
    }).isRequired,
    loading: PropTypes.bool.isRequired,
  }).isRequired,
  client_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default connect(mapStateToProps)(ClientOrders);
